import {
  isScriptAtmosphereRole,
  isScriptDispatcherRole,
  hasScriptSheetContentInRow,
  SCRIPT_ATMOSPHERE_ROLE_PREFIX,
  SCRIPT_ROLE_COLUMN_INDEX,
  type ParsedScriptSheetRow,
} from "./feishuScriptSheet";
import {
  findRoleColumnMergeAnchorRow,
  mergeRangeCoversCell,
  type FeishuMergeRange,
} from "./feishuSheetMerges";
import {
  classifyScriptContentBeat,
  type PriorRoleAssignment,
  type ScriptContentBeatType,
} from "./scriptRoleDiversity";
import type { ScriptRolePersona } from "./scriptRoleAssignment";

export const SCRIPT_CONTENT_COLUMN = "C";
export const SCRIPT_ASSIGNMENT_NUMBER_COLUMN = "D";
export const SCRIPT_ASSIGNMENT_NUMBER_FORE_COLOR = "#6467F1";
export const ASSIGNMENT_NEIGHBOR_RADIUS = 3;
/** 发言数 ≤ 此值时跳过画像 LLM，直接用原话作为画像 */
export const INLINE_PERSONA_UTTERANCE_THRESHOLD = 1;
/** 单句低于此字数视为弱信号，匹配时降权 story/tone */
export const WEAK_SINGLE_UTTERANCE_MAX_CHARS = 10;

export interface ScriptAssignmentUtterance {
  rowIndex: number;
  rowNumber: number;
  role: string;
  roleIsInherited: boolean;
  tempRoleId?: string;
  content: string;
  isAtmosphere: boolean;
}

export interface ScriptTempRoleBundle {
  tempRoleId: string;
  speakerLabel: string;
  utterances: ScriptAssignmentUtterance[];
  fullText: string;
  neighborContext: string;
  contentBeat: ScriptContentBeatType;
  firstRowIndex: number;
  lastRowIndex: number;
}

/** 临时角色在分配流程中的元信息（含 C 列需发内容行号） */
export interface ScriptTempRoleAssignmentMeta {
  tempRoleId: string;
  speakerLabel: string;
  utteranceCount: number;
  contentBeat: ScriptContentBeatType;
  firstRowIndex: number;
  lastRowIndex: number;
  contentRowNumbers: number[];
}

export function mapScriptTempRoleBundleToAssignmentMeta(
  bundle: ScriptTempRoleBundle
): ScriptTempRoleAssignmentMeta {
  return {
    tempRoleId: bundle.tempRoleId,
    speakerLabel: bundle.speakerLabel,
    utteranceCount: bundle.utterances.length,
    contentBeat: bundle.contentBeat,
    firstRowIndex: bundle.firstRowIndex,
    lastRowIndex: bundle.lastRowIndex,
    contentRowNumbers: bundle.utterances.map((item) => item.rowNumber),
  };
}

export function resolveScriptTempRoleFullTexts(
  parsedRows: ParsedScriptSheetRow[],
  imageTextsByFileToken: Record<string, string>,
  options: AssignmentEligibilityOptions = {}
): Map<string, string> {
  const bundles = buildScriptTempRoleBundles(parsedRows, imageTextsByFileToken, options);
  return new Map(bundles.map((bundle) => [bundle.tempRoleId, bundle.fullText]));
}

export function enrichPriorAssignmentsWithFullText(
  priorAssignments: PriorRoleAssignment[],
  fullTextByTempRoleId: Map<string, string>
): PriorRoleAssignment[] {
  return priorAssignments.map((item) => ({
    ...item,
    fullText: item.fullText ?? fullTextByTempRoleId.get(item.tempRoleId) ?? "",
  }));
}

/** 格式化需发内容所在行号，如「第 8-10 行」 */
export function formatScriptContentRowRange(rowNumbers: number[]): string {
  if (!rowNumbers.length) return "—";
  const sorted = [...rowNumbers].sort((left, right) => left - right);
  if (sorted.length === 1) {
    return `第 ${sorted[0]} 行`;
  }
  const isConsecutive = sorted.every(
    (rowNumber, index) => index === 0 || rowNumber === sorted[index - 1]! + 1
  );
  if (isConsecutive) {
    return `第 ${sorted[0]}-${sorted[sorted.length - 1]} 行`;
  }
  return `第 ${sorted.join("、")} 行`;
}

export interface BuildAssignmentInput {
  parsedRows: ParsedScriptSheetRow[];
  imageTextsByFileToken: Record<string, string>;
}

export class MissingImageUnderstandingError extends Error {
  readonly fileTokens: string[];

  constructor(fileTokens: string[]) {
    super(`以下图片尚未完成理解：${fileTokens.join(", ")}`);
    this.name = "MissingImageUnderstandingError";
    this.fileTokens = fileTokens;
  }
}

export function extractTempRoleSuffix(role: string): string | null {
  if (!isScriptAtmosphereRole(role)) return null;
  const suffix = role.slice(SCRIPT_ATMOSPHERE_ROLE_PREFIX.length).trim();
  return suffix || null;
}

export function resolveTempRoleId(input: {
  role: string;
  roleIsInherited: boolean;
  nextAutoIndex: number;
}): { tempRoleId: string; nextAutoIndex: number } {
  const suffix = extractTempRoleSuffix(input.role);
  if (suffix) {
    return { tempRoleId: suffix, nextAutoIndex: input.nextAutoIndex };
  }

  if (!input.roleIsInherited) {
    const tempRoleId = `C${input.nextAutoIndex}`;
    return { tempRoleId, nextAutoIndex: input.nextAutoIndex + 1 };
  }

  throw new Error("继承行缺少可归属的临时角色");
}

export function resolveColumnCText(
  row: ParsedScriptSheetRow,
  imageTextsByFileToken: Record<string, string>
): string | null {
  const imageToken = row.cellImages[SCRIPT_CONTENT_COLUMN];
  const text = (row.cells[SCRIPT_CONTENT_COLUMN] || "").trim();

  if (imageToken) {
    const description = imageTextsByFileToken[imageToken]?.trim();
    if (!description) {
      return null;
    }
    return `[图片] ${description}`;
  }

  if (!text) {
    return null;
  }

  return text;
}

export function collectAtmosphereContentImageTokens(
  rows: ParsedScriptSheetRow[],
  options: AssignmentEligibilityOptions = {}
): string[] {
  const eligibility = buildAssignmentRowEligibility(rows, options);
  const tokens = new Set<string>();
  for (const row of rows) {
    if (!isScriptAtmosphereRole(row.role)) continue;
    if (!eligibility.get(row.rowIndex)?.eligible) continue;
    const token = row.cellImages[SCRIPT_CONTENT_COLUMN];
    if (token) tokens.add(token);
  }
  return [...tokens];
}

export function buildScriptAssignmentUtterances(
  parsedRows: ParsedScriptSheetRow[],
  imageTextsByFileToken: Record<string, string>,
  options: AssignmentEligibilityOptions = {}
): ScriptAssignmentUtterance[] {
  const sortedRows = [...parsedRows].sort((a, b) => a.rowIndex - b.rowIndex);
  const eligibility = buildAssignmentRowEligibility(parsedRows, options);
  const utterances: ScriptAssignmentUtterance[] = [];

  for (const row of sortedRows) {
    const content = resolveColumnCText(row, imageTextsByFileToken);
    if (!content) continue;

    const isAtmosphere = isScriptAtmosphereRole(row.role);
    let tempRoleId: string | undefined;

    if (isAtmosphere) {
      const rowEligibility = eligibility.get(row.rowIndex);
      if (!rowEligibility?.eligible) continue;
      tempRoleId = rowEligibility.tempRoleId;
      if (!tempRoleId) {
        throw new Error(`第 ${row.rowNumber} 行无法确定临时角色`);
      }
    }

    utterances.push({
      rowIndex: row.rowIndex,
      rowNumber: row.rowNumber,
      role: row.role,
      roleIsInherited: row.roleIsInherited,
      tempRoleId,
      content,
      isAtmosphere,
    });
  }

  const missingTokens = collectAtmosphereContentImageTokens(parsedRows, options).filter(
    (token) => !imageTextsByFileToken[token]?.trim()
  );
  if (missingTokens.length > 0) {
    throw new MissingImageUnderstandingError(missingTokens);
  }

  return utterances;
}

function formatUtteranceLine(item: ScriptAssignmentUtterance) {
  const roleLabel = item.tempRoleId ? `气氛员${item.tempRoleId}` : item.role;
  return `${roleLabel}：${item.content}`;
}

function buildNeighborContext(
  utterances: ScriptAssignmentUtterance[],
  focusIndexes: number[]
): string {
  const indexSet = new Set<number>();
  for (const index of focusIndexes) {
    for (let offset = -ASSIGNMENT_NEIGHBOR_RADIUS; offset <= ASSIGNMENT_NEIGHBOR_RADIUS; offset += 1) {
      const target = index + offset;
      if (target >= 0 && target < utterances.length) {
        indexSet.add(target);
      }
    }
  }

  return [...indexSet]
    .sort((a, b) => a - b)
    .map((index) => formatUtteranceLine(utterances[index]!))
    .join("\n");
}

export function shouldUseInlinePersona(utteranceCount: number) {
  return utteranceCount <= INLINE_PERSONA_UTTERANCE_THRESHOLD;
}

export function isWeakSingleUtterance(content: string) {
  return content.trim().length > 0 && content.trim().length <= WEAK_SINGLE_UTTERANCE_MAX_CHARS;
}

export function buildInlineScriptRolePersona(bundle: ScriptTempRoleBundle): {
  personaSummary: string;
  toneTags: string[];
  topicTags: string[];
  sampleQuotes: string[];
} {
  const quote = bundle.fullText.trim();
  return {
    personaSummary: quote,
    toneTags: [],
    topicTags: [],
    sampleQuotes: quote ? [quote] : [],
  };
}

export function buildInlineScriptRolePersonaFromBundle(bundle: ScriptTempRoleBundle): ScriptRolePersona {
  return {
    ...buildInlineScriptRolePersona(bundle),
    tempRoleId: bundle.tempRoleId,
  };
}

export function buildInlineScriptRolePersonasByTempRoleId(
  bundles: ScriptTempRoleBundle[]
): Record<string, ScriptRolePersona> {
  const personas: Record<string, ScriptRolePersona> = {};
  for (const bundle of bundles) {
    if (!shouldUseInlinePersona(bundle.utterances.length)) continue;
    personas[bundle.tempRoleId] = buildInlineScriptRolePersonaFromBundle(bundle);
  }
  return personas;
}

export function buildScriptTempRoleBundles(
  parsedRows: ParsedScriptSheetRow[],
  imageTextsByFileToken: Record<string, string>,
  options: AssignmentEligibilityOptions = {}
): ScriptTempRoleBundle[] {
  const utterances = buildScriptAssignmentUtterances(parsedRows, imageTextsByFileToken, options);
  const atmosphereUtterances = utterances.filter((item) => item.isAtmosphere && item.tempRoleId);
  const grouped = new Map<string, ScriptAssignmentUtterance[]>();

  for (const item of atmosphereUtterances) {
    const key = item.tempRoleId!;
    const list = grouped.get(key) || [];
    list.push(item);
    grouped.set(key, list);
  }

  return [...grouped.entries()]
    .map(([tempRoleId, roleUtterances]) => {
      const focusIndexes = roleUtterances.map((item) =>
        utterances.findIndex((candidate) => candidate.rowIndex === item.rowIndex)
      );
      const fullText = roleUtterances.map((item) => item.content).join("\n\n");
      const rowIndexes = roleUtterances.map((item) => item.rowIndex);
      return {
        tempRoleId,
        speakerLabel: tempRoleId.startsWith("C") ? `气氛员${tempRoleId}` : `气氛员${tempRoleId}`,
        utterances: roleUtterances,
        fullText,
        neighborContext: buildNeighborContext(utterances, focusIndexes),
        contentBeat: classifyScriptContentBeat(fullText),
        firstRowIndex: Math.min(...rowIndexes),
        lastRowIndex: Math.max(...rowIndexes),
      };
    })
    .sort((left, right) => left.firstRowIndex - right.firstRowIndex);
}

export function buildAssignmentPreviewRows(
  parsedRows: ParsedScriptSheetRow[],
  assignments: Record<string, { nickname: string; number: string }>,
  options: AssignmentEligibilityOptions = {}
): Array<ParsedScriptSheetRow & { assignedRole: string }> {
  const overwriteMode = options.overwriteMode === true;
  const bundles = new Map<string, string>();
  for (const [tempRoleId, member] of Object.entries(assignments)) {
    const label = member.number
      ? `${member.number} ${member.nickname}`.trim()
      : member.nickname;
    bundles.set(tempRoleId, label);
  }

  const eligibility = buildAssignmentRowEligibility(parsedRows, options);
  const lastScriptRow = getScriptSheetLastContentRowIndex(parsedRows);

  return parsedRows.map((row) => {
    const rowEligibility = eligibility.get(row.rowIndex);
    const tempRoleId = rowEligibility?.tempRoleId;
    const assignedNumber = extractPureMemberNumber(row.cells[SCRIPT_ASSIGNMENT_NUMBER_COLUMN] || "");

    if (!overwriteMode && rowEligibility?.preAssigned && assignedNumber) {
      return {
        ...row,
        assignedRole: assignedNumber,
      };
    }

    const shouldShowAssignment =
      tempRoleId !== undefined &&
      bundles.has(tempRoleId) &&
      shouldWriteAssignmentToRow(row) &&
      (lastScriptRow < 0 || row.rowIndex <= lastScriptRow);

    const assignedRole =
      shouldShowAssignment
        ? bundles.get(tempRoleId)!
        : isScriptDispatcherRole(row.role)
          ? row.role
          : row.role;
    return {
      ...row,
      assignedRole,
    };
  });
}

export function extractPureMemberNumber(number: string): string | null {
  const trimmed = number.trim();
  if (!trimmed) return null;
  if (/^\d+$/.test(trimmed)) return trimmed;
  const match = trimmed.match(/\d+/);
  return match?.[0] ?? null;
}

export function hasAssignedMemberNumberInColumnD(row: ParsedScriptSheetRow): boolean {
  return extractPureMemberNumber(row.cells[SCRIPT_ASSIGNMENT_NUMBER_COLUMN] || "") !== null;
}

export function hasScriptContentInRow(row: ParsedScriptSheetRow): boolean {
  return hasScriptSheetContentInRow(row);
}

/** 剧本最后一条有 C 列内容的行（全局上界，防止写到表格尾部空行） */
export function getScriptSheetLastContentRowIndex(
  parsedRows: ParsedScriptSheetRow[]
): number {
  let last = -1;
  for (const row of parsedRows) {
    if (hasScriptContentInRow(row)) {
      last = Math.max(last, row.rowIndex);
    }
  }
  return last;
}

/** 是否应将分配编号写回该行的 D 列：仅有 C 列发言内容的行才写 */
export function shouldWriteAssignmentToRow(row: ParsedScriptSheetRow): boolean {
  return hasScriptContentInRow(row);
}

/** 每个临时角色最后一条有 C 列内容的行 */
export function buildLastContentRowIndexByTempRole(
  parsedRows: ParsedScriptSheetRow[],
  options: AssignmentEligibilityOptions = {}
): Map<string, number> {
  const eligibility = buildAssignmentRowEligibility(parsedRows, options);
  const result = new Map<string, number>();

  for (const row of parsedRows) {
    if (!isScriptAtmosphereRole(row.role)) continue;
    if (!hasScriptContentInRow(row)) continue;

    const tempRoleId = eligibility.get(row.rowIndex)?.tempRoleId;
    if (!tempRoleId) continue;

    const prev = result.get(tempRoleId) ?? -1;
    result.set(tempRoleId, Math.max(prev, row.rowIndex));
  }

  return result;
}

export interface AssignmentEligibilityOptions {
  /** 为 true 时，D 列已有编号的角色块仍参与匹配，写回时直接覆盖 */
  overwriteMode?: boolean;
}

export interface AssignmentRowEligibility {
  eligible: boolean;
  preAssigned: boolean;
  tempRoleId?: string;
}

/** D 列已有编号的气氛员行及其同块继承行，默认不参与剧本匹配；覆盖模式下仍参与 */
export function buildAssignmentRowEligibility(
  parsedRows: ParsedScriptSheetRow[],
  options: AssignmentEligibilityOptions = {}
): Map<number, AssignmentRowEligibility> {
  const overwriteMode = options.overwriteMode === true;
  const sortedRows = [...parsedRows].sort((a, b) => a.rowIndex - b.rowIndex);
  const result = new Map<number, AssignmentRowEligibility>();
  let nextAutoIndex = 1;
  let currentTempRoleId: string | undefined;
  let skipBlock = false;

  for (const row of sortedRows) {
    if (!isScriptAtmosphereRole(row.role)) {
      result.set(row.rowIndex, { eligible: false, preAssigned: false });
      currentTempRoleId = undefined;
      skipBlock = false;
      continue;
    }

    const suffix = extractTempRoleSuffix(row.role);
    const isNewBlock = Boolean(suffix) || !row.roleIsInherited;
    const rowHasAssignedNumber = hasAssignedMemberNumberInColumnD(row);

    if (isNewBlock) {
      if (suffix) {
        currentTempRoleId = suffix;
      } else {
        const resolved = resolveTempRoleId({
          role: row.role,
          roleIsInherited: row.roleIsInherited,
          nextAutoIndex,
        });
        currentTempRoleId = resolved.tempRoleId;
        nextAutoIndex = resolved.nextAutoIndex;
      }
      skipBlock = overwriteMode ? false : rowHasAssignedNumber;
    }

    const preAssigned = overwriteMode ? false : rowHasAssignedNumber || skipBlock;
    result.set(row.rowIndex, {
      eligible: !preAssigned && Boolean(currentTempRoleId),
      preAssigned,
      tempRoleId: currentTempRoleId,
    });
  }

  return result;
}

export function buildRoleAssignmentReplacements(
  parsedRows: ParsedScriptSheetRow[],
  assignments: Record<string, { nickname: string; number: string }>,
  options: AssignmentEligibilityOptions = {}
): Array<{ rowIndex: number; value: string }> {
  const overwriteMode = options.overwriteMode === true;
  const numberByTempRole = new Map<string, string>();
  for (const [tempRoleId, member] of Object.entries(assignments)) {
    const pureNumber = extractPureMemberNumber(member.number);
    if (pureNumber) {
      numberByTempRole.set(tempRoleId, pureNumber);
    }
  }

  const eligibility = buildAssignmentRowEligibility(parsedRows, options);
  const lastScriptRow = getScriptSheetLastContentRowIndex(parsedRows);

  const replacements: Array<{ rowIndex: number; value: string }> = [];

  for (const row of parsedRows) {
    if (!isScriptAtmosphereRole(row.role)) continue;
    if (lastScriptRow >= 0 && row.rowIndex > lastScriptRow) continue;
    if (!shouldWriteAssignmentToRow(row)) continue;

    const rowEligibility = eligibility.get(row.rowIndex);
    if (!overwriteMode && rowEligibility?.preAssigned) continue;

    const tempRoleId = rowEligibility?.tempRoleId;
    if (!tempRoleId) continue;

    const pureNumber = numberByTempRole.get(tempRoleId);
    if (!pureNumber) continue;

    const currentValue = (row.cells[SCRIPT_ASSIGNMENT_NUMBER_COLUMN] || "").trim();
    if (!overwriteMode && currentValue === pureNumber) continue;

    replacements.push({
      rowIndex: row.rowIndex,
      value: pureNumber,
    });
  }

  return replacements;
}

export interface RoleAssignmentReplacementBatch {
  startRowIndex: number;
  endRowIndex: number;
  values: string[][];
}

/** Groups adjacent D-column writes while rejecting ambiguous duplicate rows. */
export function groupRoleAssignmentReplacementBatches(
  replacements: Array<{ rowIndex: number; value: string }>
): RoleAssignmentReplacementBatch[] {
  const sorted = [...replacements].sort((left, right) => left.rowIndex - right.rowIndex);
  const batches: RoleAssignmentReplacementBatch[] = [];

  for (const item of sorted) {
    const previous = batches.at(-1);
    if (previous && item.rowIndex === previous.endRowIndex) {
      throw new Error(`写回数据包含重复行：第 ${item.rowIndex + 1} 行`);
    }
    if (previous && item.rowIndex === previous.endRowIndex + 1) {
      previous.endRowIndex = item.rowIndex;
      previous.values.push([item.value]);
      continue;
    }
    batches.push({
      startRowIndex: item.rowIndex,
      endRowIndex: item.rowIndex,
      values: [[item.value]],
    });
  }

  return batches;
}

export interface ScriptRoleBlockSummary {
  tempRoleId: string;
  speakerLabel: string;
  startRowNumber: number;
  endRowNumber: number;
  anchorRowNumber: number;
  mergeSource: "api" | "heuristic";
  /** 飞书 B 列合并行号（1-based），无 API 合并时为空 */
  bMergeRange?: string;
  contentRowNumbers: number[];
}

export function formatRoleColumnMergeRange(merge: FeishuMergeRange): string {
  const start = merge.startRowIndex + 1;
  const end = merge.endRowIndex + 1;
  return start === end ? `${start}` : `${start}-${end}`;
}

function findRoleColumnMergeForRow(
  merges: FeishuMergeRange[],
  rowIndex: number
): FeishuMergeRange | undefined {
  return merges.find((merge) =>
    mergeRangeCoversCell(merge, rowIndex, SCRIPT_ROLE_COLUMN_INDEX)
  );
}

/** 根据解析结果汇总临时角色块，供前端核对剧本划分 */
export function buildScriptRoleBlockSummaries(
  allParsedRows: ParsedScriptSheetRow[],
  roleColumnMerges: FeishuMergeRange[] = [],
  options: AssignmentEligibilityOptions = {}
): ScriptRoleBlockSummary[] {
  const sortedRows = [...allParsedRows].sort((a, b) => a.rowIndex - b.rowIndex);
  const eligibility = buildAssignmentRowEligibility(sortedRows, options);
  const blocks = new Map<string, ScriptRoleBlockSummary>();

  for (const row of sortedRows) {
    if (!isScriptAtmosphereRole(row.role)) continue;

    const tempRoleId = eligibility.get(row.rowIndex)?.tempRoleId;
    if (!tempRoleId) continue;

    const merge = findRoleColumnMergeForRow(roleColumnMerges, row.rowIndex);
    const mergeAnchorRow = findRoleColumnMergeAnchorRow(roleColumnMerges, row.rowIndex);
    let block = blocks.get(tempRoleId);

    if (!block) {
      block = {
        tempRoleId,
        speakerLabel: tempRoleId.startsWith("C") ? `气氛员${tempRoleId}` : `气氛员${tempRoleId}`,
        startRowNumber: row.rowNumber,
        endRowNumber: row.rowNumber,
        anchorRowNumber:
          mergeAnchorRow !== undefined ? mergeAnchorRow + 1 : row.rowNumber,
        mergeSource: merge ? "api" : "heuristic",
        bMergeRange: merge ? formatRoleColumnMergeRange(merge) : undefined,
        contentRowNumbers: [],
      };
      blocks.set(tempRoleId, block);
    }

    block.endRowNumber = row.rowNumber;
    if (hasScriptSheetContentInRow(row)) {
      block.contentRowNumbers.push(row.rowNumber);
    }
  }

  return [...blocks.values()].sort((left, right) => left.startRowNumber - right.startRowNumber);
}
